import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  Printer,
  Info,
  ArrowRight,
  Users,
  Briefcase,
  Bed,
  Clock8,
  ShieldAlert,
  ChevronDown,
} from 'lucide-react';

// ---------- Formatting helpers ----------
const usd = (n) => {
  if (!Number.isFinite(n)) return '$0';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${n < 0 ? '-' : ''}$${(abs / 1_000_000).toFixed(2)}M`;
  return `${n < 0 ? '-' : ''}$${Math.round(abs).toLocaleString('en-US')}`;
};
const pct = (n) => `${(Number.isFinite(n) ? n : 0).toFixed(1)}%`;
const months = (n) => {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n > 240) return '20+ yrs';
  if (n < 1) return '< 1 mo';
  return `${n.toFixed(1)} mo`;
};

const WORKFORCE_OPTIONS = [
  { key: 'frontline', label: 'Frontline / Operational', pct: 0.4 },
  { key: 'professional', label: 'Professional / Technical', pct: 0.8 },
  { key: 'manager', label: 'Manager / Leader', pct: 2.0 },
  { key: 'custom', label: 'Custom (enter your own %)', pct: null },
];

// ---------- Core calculation (pure) ----------
export function computeROI(inputs) {
  const {
    employees,
    hourlyWage,
    benefits,
    hoursPerDay,
    turnoverRate,
    workforceType,
    customReplacementPct,
    lostWorkdays,
    overtimeCost,
    tempLaborCost,
    safetyCost,
    targetTurnoverReduction,
    targetWorkdaysRecovered,
    targetOvertimeReduction,
    targetTempReduction,
    targetSafetyReduction,
    investment,
  } = inputs;

  const wfOpt = WORKFORCE_OPTIONS.find((o) => o.key === workforceType);
  const replacementPct =
    workforceType === 'custom'
      ? Math.max(0, customReplacementPct || 0)
      : wfOpt
      ? wfOpt.pct
      : 0.4;

  const annualSalary = hourlyWage * 2080;
  const replacementCostPerExit = annualSalary * replacementPct;
  const annualExits = employees * turnoverRate;
  const turnoverCostExposure = annualExits * replacementCostPerExit;

  const hourlyEmployerComp = hourlyWage + benefits;
  const lostWorkdayExposure =
    employees * lostWorkdays * hoursPerDay * hourlyEmployerComp;

  const totalCostExposure =
    turnoverCostExposure +
    lostWorkdayExposure +
    overtimeCost +
    tempLaborCost +
    safetyCost;

  const potentialTurnoverSavings = turnoverCostExposure * targetTurnoverReduction;
  const potentialRecoveredWorkdayValue = lostWorkdayExposure * targetWorkdaysRecovered;
  const potentialOvertimeSavings = overtimeCost * targetOvertimeReduction;
  const potentialTempLaborSavings = tempLaborCost * targetTempReduction;
  const potentialSafetySavings = safetyCost * targetSafetyReduction;

  const financialOpportunity =
    potentialTurnoverSavings +
    potentialRecoveredWorkdayValue +
    potentialOvertimeSavings +
    potentialTempLaborSavings +
    potentialSafetySavings;

  const netBenefit = financialOpportunity - investment;
  const roi = investment > 0 ? (netBenefit / investment) * 100 : 0;
  const paybackMonths = financialOpportunity > 0 ? (investment / financialOpportunity) * 12 : Infinity;
  const breakEvenPct = financialOpportunity > 0 ? (investment / financialOpportunity) * 100 : Infinity;

  return {
    annualSalary,
    replacementCostPerExit,
    annualExits,
    turnoverCostExposure,
    hourlyEmployerComp,
    lostWorkdayExposure,
    overtimeCostExposure: overtimeCost,
    tempLaborCostExposure: tempLaborCost,
    safetyCostExposure: safetyCost,
    totalCostExposure,
    potentialTurnoverSavings,
    potentialRecoveredWorkdayValue,
    potentialOvertimeSavings,
    potentialTempLaborSavings,
    potentialSafetySavings,
    financialOpportunity,
    netBenefit,
    roi,
    paybackMonths,
    breakEvenPct,
    replacementPctApplied: replacementPct,
  };
}

// ---------- Primitives ----------
function Field({ label, hint, optional, required, children, testId }) {
  return (
    <label className="block" data-testid={testId ? `${testId}-field` : undefined}>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="font-display uppercase tracking-[0.16em] text-[11px] font-bold text-[#0A0A0A]">
          {label}
          {required && (
            <span className="text-[#DC2626] ml-1" aria-hidden="true">*</span>
          )}
        </span>
        {optional && (
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#888]">Optional</span>
        )}
      </div>
      {children}
      {hint && <div className="mt-1.5 text-[11px] text-[#777] leading-snug">{hint}</div>}
    </label>
  );
}

function NumInput({ value, onChange, min = 0, step = 1, suffix, placeholder, testId }) {
  return (
    <div className="relative">
      <input
        type="number"
        inputMode="decimal"
        className="brp-input brp-input-light pr-10"
        value={value}
        min={min}
        step={step}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
      {suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#888] text-sm font-display uppercase tracking-widest">
          {suffix}
        </span>
      )}
    </div>
  );
}

function ResultTile({ label, value, tone, testId, big = false, hint }) {
  const tones = {
    green: { bar: '#16A34A', text: '#0A7A32', bg: '#F0FDF4' },
    red: { bar: '#DC2626', text: '#B4160B', bg: '#FEF2F2' },
    gold: { bar: '#D4AF37', text: '#8A6B10', bg: '#FFFBEB' },
    blue: { bar: '#2563EB', text: '#1D4ED8', bg: '#EFF6FF' },
    neutral: { bar: '#9CA3AF', text: '#0A0A0A', bg: '#F9FAFB' },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div
      className={`relative border border-[#E5E5E5] p-6 md:p-7 ${big ? 'md:p-10' : ''}`}
      style={{ background: t.bg }}
      data-testid={testId}
    >
      <div
        className="absolute top-0 left-0 h-1"
        style={{ width: big ? '96px' : '48px', background: t.bar }}
      />
      <div className="font-display uppercase tracking-[0.2em] text-[10px] md:text-[11px] font-bold text-[#0A0A0A]/70">
        {label}
      </div>
      <div
        className={`font-display font-bold leading-none tracking-tight mt-3 ${
          big ? 'text-5xl md:text-6xl lg:text-7xl' : 'text-3xl md:text-4xl'
        }`}
        style={{ color: t.text }}
      >
        {value}
      </div>
      {hint && <div className="mt-3 text-[11px] text-[#666] uppercase tracking-widest">{hint}</div>}
    </div>
  );
}

function LegendChip({ color, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block w-3 h-3" style={{ background: color }} />
      <span className="text-[11px] font-display uppercase tracking-[0.2em] text-[#0A0A0A]/80">
        {label}
      </span>
    </div>
  );
}

function Section({ icon: Icon, title, defaultOpen = true, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#E5E5E5] bg-white" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-6 py-5 text-left"
        data-testid={`${testId}-toggle`}
      >
        {Icon && (
          <span className="w-9 h-9 flex items-center justify-center border border-[#D4AF37] flex-shrink-0">
            <Icon size={16} strokeWidth={1.75} className="text-[#D4AF37]" />
          </span>
        )}
        <span className="font-display uppercase tracking-wide text-sm md:text-base font-bold flex-1">
          {title}
        </span>
        <ChevronDown
          size={18}
          className={`text-[#0A0A0A]/60 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <div className="px-6 pb-6 border-t border-[#F0F0F0]" data-testid={`${testId}-body`}>{children}</div>}
    </div>
  );
}

// ---------- Main component ----------
export default function WorkforceROICalculator({
  title = 'Free Workforce Performance ROI Calculator',
  subtitle = 'What is workforce performance costing your organization? Estimate turnover exposure, lost workday costs, potential financial opportunity, ROI, and payback using your organization\u2019s actual workforce data.',
  sourcePage = 'roi-calculator',
  testIdPrefix = 'roi',
}) {
  // Core
  const [employees, setEmployees] = useState('250');
  const [hourlyWage, setHourlyWage] = useState('28');
  const [benefits, setBenefits] = useState('8');
  const [hoursPerDay, setHoursPerDay] = useState('8');

  // Turnover
  const [turnoverRate, setTurnoverRate] = useState(''); // NO prefill
  const [workforceType, setWorkforceType] = useState('frontline');
  const [customReplacementPct, setCustomReplacementPct] = useState('60');
  const [daysToFill, setDaysToFill] = useState(''); // NO prefill

  // Absence
  const [lostWorkdays, setLostWorkdays] = useState('0');

  // Coverage
  const [overtimeCost, setOvertimeCost] = useState('0');
  const [tempLaborCost, setTempLaborCost] = useState('0');

  // Safety
  const [safetyCost, setSafetyCost] = useState('0');

  // Improvement Scenario
  const [targetTurnoverReduction, setTargetTurnoverReduction] = useState('25');
  const [targetWorkdaysRecovered, setTargetWorkdaysRecovered] = useState('25');
  const [targetOvertimeReduction, setTargetOvertimeReduction] = useState('');
  const [targetTempReduction, setTargetTempReduction] = useState('');
  const [targetSafetyReduction, setTargetSafetyReduction] = useState('');
  const [investment, setInvestment] = useState('100000');

  const [showMethodology, setShowMethodology] = useState(false);

  const parsed = useMemo(() => {
    const n = (v, fb = 0) => {
      const p = parseFloat(v);
      return Number.isFinite(p) ? p : fb;
    };
    return {
      employees: Math.max(0, n(employees)),
      hourlyWage: Math.max(0, n(hourlyWage)),
      benefits: Math.max(0, n(benefits)),
      hoursPerDay: Math.max(0, n(hoursPerDay)),
      turnoverRate: Math.max(0, n(turnoverRate)) / 100,
      workforceType,
      customReplacementPct: Math.max(0, n(customReplacementPct)) / 100,
      daysToFill: Math.max(0, n(daysToFill)),
      lostWorkdays: Math.max(0, n(lostWorkdays)),
      overtimeCost: Math.max(0, n(overtimeCost)),
      tempLaborCost: Math.max(0, n(tempLaborCost)),
      safetyCost: Math.max(0, n(safetyCost)),
      targetTurnoverReduction: Math.min(100, Math.max(0, n(targetTurnoverReduction))) / 100,
      targetWorkdaysRecovered: Math.min(100, Math.max(0, n(targetWorkdaysRecovered))) / 100,
      targetOvertimeReduction: Math.min(100, Math.max(0, n(targetOvertimeReduction))) / 100,
      targetTempReduction: Math.min(100, Math.max(0, n(targetTempReduction))) / 100,
      targetSafetyReduction: Math.min(100, Math.max(0, n(targetSafetyReduction))) / 100,
      investment: Math.max(0, n(investment)),
    };
  }, [
    employees, hourlyWage, benefits, hoursPerDay,
    turnoverRate, workforceType, customReplacementPct, daysToFill,
    lostWorkdays, overtimeCost, tempLaborCost, safetyCost,
    targetTurnoverReduction, targetWorkdaysRecovered,
    targetOvertimeReduction, targetTempReduction, targetSafetyReduction,
    investment,
  ]);

  const result = useMemo(() => computeROI(parsed), [parsed]);

  const scenario = (multiplier) => {
    const cap = (v) => Math.min(1, v * multiplier);
    return computeROI({
      ...parsed,
      targetTurnoverReduction: cap(parsed.targetTurnoverReduction),
      targetWorkdaysRecovered: cap(parsed.targetWorkdaysRecovered),
      targetOvertimeReduction: cap(parsed.targetOvertimeReduction),
      targetTempReduction: cap(parsed.targetTempReduction),
      targetSafetyReduction: cap(parsed.targetSafetyReduction),
    });
  };
  const conservative = useMemo(() => scenario(0.5), [parsed]);
  const target = useMemo(() => scenario(1.0), [parsed]);
  const highImpact = useMemo(() => scenario(1.5), [parsed]);

  const hasOpportunity = result.financialOpportunity > 0;
  const netPositive = result.netBenefit >= 0;

  const handlePrint = () => {
    if (typeof window !== 'undefined') window.print();
  };

  return (
    <div data-testid={`${testIdPrefix}-container`}>
      {/* Heading */}
      <div className="max-w-3xl">
        <p className="brp-overline">ROI Calculator</p>
        <h2 className="mt-4 font-display uppercase tracking-tight font-bold text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-[#0A0A0A]">
          {title}
        </h2>
        <p className="mt-6 text-base md:text-lg leading-relaxed text-[#555]">{subtitle}</p>
        <span className="brp-divider-gold mt-8" />
      </div>

      {/* Legend */}
      <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid={`${testIdPrefix}-legend`}>
        <LegendChip color="#16A34A" label="Green = Financial Gain" />
        <LegendChip color="#DC2626" label="Red = Cost Exposure" />
        <LegendChip color="#D4AF37" label="Gold = Return" />
        <LegendChip color="#2563EB" label="Blue = Payback Time" />
        <span className="text-[11px] font-display uppercase tracking-[0.2em] text-[#0A0A0A]/80 flex items-center gap-1">
          <span className="text-[#DC2626]">*</span>
          <span>= Required Field</span>
        </span>
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Inputs */}
        <div className="lg:col-span-5 space-y-4">
          <Section icon={Users} title="Core Workforce Data" defaultOpen testId={`${testIdPrefix}-sec-core`}>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Number of Employees" required testId={`${testIdPrefix}-employees`}>
                <NumInput value={employees} onChange={setEmployees} step={1} testId={`${testIdPrefix}-input-employees`} />
              </Field>
              <Field label="Average Hourly Wage" required testId={`${testIdPrefix}-wage`}>
                <NumInput value={hourlyWage} onChange={setHourlyWage} step={0.5} suffix="USD/HR" testId={`${testIdPrefix}-input-wage`} />
              </Field>
              <Field label="Employer Benefits Cost / Hr" required hint="Health, retirement, taxes (BLS employer compensation)" testId={`${testIdPrefix}-benefits`}>
                <NumInput value={benefits} onChange={setBenefits} step={0.5} suffix="USD/HR" testId={`${testIdPrefix}-input-benefits`} />
              </Field>
              <Field label="Paid Hours / Workday" required testId={`${testIdPrefix}-hours`}>
                <NumInput value={hoursPerDay} onChange={setHoursPerDay} step={0.5} suffix="HRS" testId={`${testIdPrefix}-input-hours`} />
              </Field>
            </div>
          </Section>

          <Section icon={Briefcase} title="Turnover & Hiring" defaultOpen testId={`${testIdPrefix}-sec-turnover`}>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="Annual Turnover Rate"
                required
                hint="Enter your organization's actual annual turnover rate."
                testId={`${testIdPrefix}-turnover`}
              >
                <NumInput
                  value={turnoverRate}
                  onChange={setTurnoverRate}
                  step={0.1}
                  suffix="%"
                  placeholder="Enter your rate"
                  testId={`${testIdPrefix}-input-turnover`}
                />
              </Field>
              <Field label="Primary Workforce Type" required testId={`${testIdPrefix}-wf-type`}>
                <select
                  className="brp-input brp-input-light"
                  value={workforceType}
                  onChange={(e) => setWorkforceType(e.target.value)}
                  data-testid={`${testIdPrefix}-input-workforce-type`}
                >
                  {WORKFORCE_OPTIONS.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                      {o.pct !== null ? ` — ${Math.round(o.pct * 100)}%` : ''}
                    </option>
                  ))}
                </select>
              </Field>
              {workforceType === 'custom' && (
                <Field
                  label="Custom Replacement Cost"
                  required
                  hint="% of annual salary to replace one exit"
                  testId={`${testIdPrefix}-custom`}
                >
                  <NumInput
                    value={customReplacementPct}
                    onChange={setCustomReplacementPct}
                    step={1}
                    suffix="%"
                    testId={`${testIdPrefix}-input-custom`}
                  />
                </Field>
              )}
              <Field
                label="Average Days to Fill an Open Position"
                hint="Optional. Use your organization's actual data when available."
                optional
                testId={`${testIdPrefix}-days-to-fill`}
              >
                <NumInput
                  value={daysToFill}
                  onChange={setDaysToFill}
                  step={1}
                  suffix="DAYS"
                  placeholder="Enter days"
                  testId={`${testIdPrefix}-input-days-to-fill`}
                />
              </Field>
            </div>
          </Section>

          <Section icon={Bed} title="Absence & Productive Capacity" defaultOpen={false} testId={`${testIdPrefix}-sec-absence`}>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="Avoidable Lost Workdays / Employee / Yr"
                hint="Optional. Use your organization's actual data when available. Default 0."
                optional
                testId={`${testIdPrefix}-lostdays`}
              >
                <NumInput value={lostWorkdays} onChange={setLostWorkdays} step={0.5} suffix="DAYS" testId={`${testIdPrefix}-input-lostdays`} />
              </Field>
            </div>
          </Section>

          <Section icon={Clock8} title="Overtime & Workforce Coverage" defaultOpen={false} testId={`${testIdPrefix}-sec-overtime`}>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="Annual Vacancy Related Overtime Cost"
                hint="Optional. Use your organization's actual data when available. Default $0."
                optional
                testId={`${testIdPrefix}-overtime`}
              >
                <NumInput value={overtimeCost} onChange={setOvertimeCost} step={1000} suffix="USD" testId={`${testIdPrefix}-input-overtime`} />
              </Field>
              <Field
                label="Annual Temporary / Contract Labor Cost"
                hint="Optional. Use your organization's actual data when available. Default $0."
                optional
                testId={`${testIdPrefix}-temp`}
              >
                <NumInput value={tempLaborCost} onChange={setTempLaborCost} step={1000} suffix="USD" testId={`${testIdPrefix}-input-temp`} />
              </Field>
            </div>
          </Section>

          <Section icon={ShieldAlert} title="Safety Costs" defaultOpen={false} testId={`${testIdPrefix}-sec-safety`}>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label={"Annual Workers\u2019 Compensation / Injury Cost"}
                hint="Optional. Use your organization's actual data when available. Default $0."
                optional
                testId={`${testIdPrefix}-safety`}
              >
                <NumInput value={safetyCost} onChange={setSafetyCost} step={1000} suffix="USD" testId={`${testIdPrefix}-input-safety`} />
              </Field>
            </div>
          </Section>

          <Section icon={TrendingUp} title="Improvement Scenario" defaultOpen testId={`${testIdPrefix}-sec-scenario`}>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Target Turnover Reduction" required testId={`${testIdPrefix}-tred`}>
                <NumInput value={targetTurnoverReduction} onChange={setTargetTurnoverReduction} step={1} suffix="%" testId={`${testIdPrefix}-input-turnover-reduction`} />
              </Field>
              <Field label="Target Lost Workdays Recovered" required testId={`${testIdPrefix}-wrec`}>
                <NumInput value={targetWorkdaysRecovered} onChange={setTargetWorkdaysRecovered} step={1} suffix="%" testId={`${testIdPrefix}-input-workdays-recovered`} />
              </Field>
              <Field label="Target Overtime Cost Reduction" optional testId={`${testIdPrefix}-tot`}>
                <NumInput value={targetOvertimeReduction} onChange={setTargetOvertimeReduction} step={1} suffix="%" placeholder="Enter %" testId={`${testIdPrefix}-input-overtime-reduction`} />
              </Field>
              <Field label="Target Temporary Labor Cost Reduction" optional testId={`${testIdPrefix}-ttl`}>
                <NumInput value={targetTempReduction} onChange={setTargetTempReduction} step={1} suffix="%" placeholder="Enter %" testId={`${testIdPrefix}-input-temp-reduction`} />
              </Field>
              <Field label="Target Safety Cost Reduction" optional testId={`${testIdPrefix}-tsc`}>
                <NumInput value={targetSafetyReduction} onChange={setTargetSafetyReduction} step={1} suffix="%" placeholder="Enter %" testId={`${testIdPrefix}-input-safety-reduction`} />
              </Field>
              <Field label="Annual Workforce Performance Investment" required testId={`${testIdPrefix}-invest`}>
                <NumInput value={investment} onChange={setInvestment} step={1000} suffix="USD" testId={`${testIdPrefix}-input-investment`} />
              </Field>
            </div>
          </Section>
        </div>

        {/* Results */}
        <div className="lg:col-span-7 space-y-6">
          <ResultTile
            testId={`${testIdPrefix}-primary`}
            big
            tone="green"
            label="Estimated Annual Financial Opportunity"
            value={usd(result.financialOpportunity)}
            hint={
              hasOpportunity
                ? 'Modeled portion of workforce cost exposure under employer-selected assumptions'
                : 'Enter improvement inputs to model opportunity'
            }
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <ResultTile
              testId={`${testIdPrefix}-cost-exposure`}
              tone="red"
              label="Total Workforce Cost Exposure"
              value={usd(result.totalCostExposure)}
              hint={`Turnover ${usd(result.turnoverCostExposure)} · Workdays ${usd(result.lostWorkdayExposure)} · OT ${usd(result.overtimeCostExposure)} · Temp ${usd(result.tempLaborCostExposure)} · Safety ${usd(result.safetyCostExposure)}`}
            />
            <ResultTile
              testId={`${testIdPrefix}-net-benefit`}
              tone={
                !hasOpportunity || result.netBenefit === 0
                  ? 'neutral'
                  : netPositive
                  ? 'green'
                  : 'red'
              }
              label="Employer Net Financial Benefit"
              value={hasOpportunity ? usd(result.netBenefit) : '—'}
              hint={
                !hasOpportunity
                  ? 'Enter improvement inputs to model net benefit'
                  : result.netBenefit === 0
                  ? 'Investment equals modeled opportunity'
                  : netPositive
                  ? 'Financial opportunity above investment — value retained after the modeled program investment'
                  : 'Modeled investment exceeds financial opportunity — reassess scenario'
              }
            />
            <ResultTile
              testId={`${testIdPrefix}-roi`}
              tone="gold"
              label="Estimated ROI"
              value={hasOpportunity && parsed.investment > 0 ? pct(result.roi) : '—'}
              hint="Net benefit vs. investment"
            />
            <ResultTile
              testId={`${testIdPrefix}-payback`}
              tone="blue"
              label="Estimated Payback Time"
              value={months(result.paybackMonths)}
              hint="Investment ÷ annual opportunity × 12"
            />
            <ResultTile
              testId={`${testIdPrefix}-breakeven`}
              tone="gold"
              label="Break Even Requirement"
              value={hasOpportunity ? pct(result.breakEvenPct) : '—'}
              hint={
                hasOpportunity
                  ? `Your organization would need to capture approximately ${pct(result.breakEvenPct)} of the modeled financial opportunity for this investment to break even.`
                  : 'Enter improvement inputs to model break-even'
              }
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handlePrint}
              className="btn-brp-outline-dark inline-flex items-center gap-2"
              data-testid={`${testIdPrefix}-print-btn`}
            >
              <Printer size={16} /> Print / Save Results
            </button>
            <div className="text-[11px] text-[#666] uppercase tracking-widest ml-auto">
              Updates live as you type
            </div>
          </div>
        </div>
      </div>

      {/* Scenarios */}
      <div className="mt-14">
        <div className="flex items-center gap-2">
          <TrendingDown size={18} className="text-[#D4AF37]" />
          <h3 className="font-display uppercase tracking-wide text-lg md:text-xl font-bold">
            Scenario Modeling
          </h3>
        </div>
        <p className="mt-2 text-sm text-[#666] max-w-2xl">
          Each scenario recalculates every improvement target — turnover, workdays, overtime, temporary labor, and safety. High Impact caps any improvement at 100%. Scenarios are business-planning ranges — not guaranteed Ball Raw Performance outcomes.
        </p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { key: 'conservative', title: 'Conservative', multiplier: '50%', r: conservative },
            { key: 'target', title: 'Target', multiplier: '100%', r: target },
            { key: 'high', title: 'High Impact', multiplier: '150% (capped 100%)', r: highImpact },
          ].map((s) => (
            <div
              key={s.key}
              className="border border-[#E5E5E5] p-6 md:p-7 bg-white"
              data-testid={`${testIdPrefix}-scenario-${s.key}`}
            >
              <div className="flex items-baseline justify-between">
                <div className="font-display uppercase tracking-wide text-lg font-bold">{s.title}</div>
                <div className="text-[10px] font-display uppercase tracking-[0.2em] text-[#888]">
                  {s.multiplier}
                </div>
              </div>
              <div className="mt-5 space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#888] font-bold">
                    Financial Opportunity
                  </div>
                  <div className="font-display text-2xl md:text-3xl font-bold text-[#0A7A32] mt-1">
                    {usd(s.r.financialOpportunity)}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#EFEFEF]">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#888] font-bold">ROI</div>
                    <div className="font-display text-lg font-bold text-[#8A6B10] mt-1">
                      {s.r.financialOpportunity === 0 || parsed.investment === 0 ? '—' : pct(s.r.roi)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#888] font-bold">Payback</div>
                    <div className="font-display text-lg font-bold text-[#1D4ED8] mt-1">
                      {months(s.r.paybackMonths)}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#888] font-bold">Break Even</div>
                    <div className="font-display text-lg font-bold text-[#8A6B10] mt-1">
                      {s.r.financialOpportunity === 0 ? '—' : pct(s.r.breakEvenPct)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Methodology */}
      <div className="mt-14 border border-[#E5E5E5] bg-white">
        <button
          type="button"
          onClick={() => setShowMethodology((v) => !v)}
          className="w-full flex items-center justify-between gap-4 p-6 md:p-7 text-left"
          data-testid={`${testIdPrefix}-methodology-toggle`}
          aria-expanded={showMethodology}
        >
          <span className="flex items-center gap-3">
            <Info size={18} className="text-[#D4AF37]" />
            <span className="font-display uppercase tracking-wide text-lg font-bold">
              Transparent Methodology & Sources
            </span>
          </span>
          <span className="font-display text-[#D4AF37] text-2xl leading-none">
            {showMethodology ? '−' : '+'}
          </span>
        </button>
        {showMethodology && (
          <div
            className="px-6 md:px-7 pb-8 text-sm leading-relaxed text-[#444]"
            data-testid={`${testIdPrefix}-methodology-body`}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-2">
              <div>
                <div className="brp-overline">Employer-Supplied Data</div>
                <ul className="mt-3 space-y-1 list-disc pl-5">
                  <li>Employee count, wages, benefits, hours</li>
                  <li>Annual turnover rate, days-to-fill</li>
                  <li>Avoidable lost workdays</li>
                  <li>Vacancy overtime, temporary labor, workers&rsquo; comp / injury costs</li>
                </ul>
              </div>
              <div>
                <div className="brp-overline">Employer-Selected Improvement Assumptions</div>
                <ul className="mt-3 space-y-1 list-disc pl-5">
                  <li>Target turnover reduction %</li>
                  <li>Target lost workdays recovered %</li>
                  <li>Target overtime, temporary labor, and safety cost reductions</li>
                  <li>Annual workforce performance investment</li>
                </ul>
              </div>
              <div>
                <div className="brp-overline">Published Benchmark Data</div>
                <ul className="mt-3 space-y-1 list-disc pl-5">
                  <li>U.S. Bureau of Labor Statistics — employer compensation context</li>
                  <li>Gallup — employee replacement cost scenarios (40% / 80% / 200%)</li>
                  <li>SHRM — recruiting cost-per-hire context</li>
                  <li>CDC / NIOSH — workforce performance & worker well-being business-case context</li>
                </ul>
              </div>
            </div>
            <p className="mt-6">
              Employer-selected improvement assumptions are not published research and are not guaranteed Ball Raw Performance outcomes. Published sources are used only where identified.
            </p>
            <div className="mt-6 p-5 border-l-4 border-[#D4AF37] bg-[#FFFBEB] text-[13px] leading-relaxed text-[#0A0A0A]">
              <strong>Disclaimer.</strong> This calculator is an educational business-planning tool, not a guarantee of financial, retention, productivity, safety, health, or workforce outcomes. Published reference values are used only where identified. Improvement percentages are employer-selected scenarios. Organizations should validate all assumptions with their HR, Finance, Operations, legal, safety, and benefits teams before making business decisions.
            </div>
          </div>
        )}
      </div>

      {/* CTA */}
      <div
        className="mt-14 bg-[#0A0A0A] text-white p-8 md:p-12 border-l-4 border-[#D4AF37] flex flex-col md:flex-row md:items-center md:justify-between gap-6"
        data-testid={`${testIdPrefix}-cta`}
      >
        <div>
          <p className="brp-overline">Next Step</p>
          <h3 className="mt-3 font-display uppercase tracking-wide text-2xl md:text-3xl font-bold">
            Want a company-specific workforce analysis?
          </h3>
        </div>
        <Link
          to={`/partnerships?utm_source=roi&utm_medium=${sourcePage}#contact`}
          className="btn-brp-primary inline-flex items-center gap-2 flex-shrink-0"
          data-testid={`${testIdPrefix}-cta-btn`}
        >
          Build a Workforce Performance Plan <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}
